import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { refId, refPayload, toDateKey } from '../../lib/format';
import {
    ESTENSIONI, PESO_MASSIMO, apriAllegato, erroreFile, leggiBase64, pesoLeggibile
} from '../../lib/allegati';
import {
    useEntityList, useEntityItem, statoLista, Field, DetailPage, DetailActions,
    ConfirmModal, messaggioErrore
} from './common';
import Select from '../../components/Select';
import RichText from '../../components/RichText';
import { eVuoto, perEditor, pulisci } from '../../lib/richtext';

const VUOTO = {
    titolo: '', versione: '1.0', testo: '', data_pubblicazione: '', idSala: '',
    attivo: 1, allegato: null
};

/**
 * Pagina di un regolamento. Il testo e' il pezzo grosso: e' quello che il
 * condomino si trova davanti al momento di prenotare, quindi qui prende
 * tutta la larghezza invece di stare in una finestrella.
 */
export default function Regolamento() {
    const { id } = useParams();
    const navigate = useNavigate();

    const nuovo = !id || id === 'nuovo';
    const { item, loading, error } = useEntityItem('regolamento', nuovo ? null : id);
    const sale = useEntityList('sala', 'nome', 'asc');
    const tutti = useEntityList('regolamento', 'data_pubblicazione', 'desc');

    const [form, setForm] = useState(nuovo ? { ...VUOTO, data_pubblicazione: toDateKey(new Date()) } : null);
    const [saving, setSaving] = useState(false);
    const [daEliminare, setDaEliminare] = useState(false);
    const [working, setWorking] = useState(false);
    const [caricando, setCaricando] = useState(false);

    useEffect(() => {
        if (!item) return;
        setForm({
            titolo: item.titolo || '',
            versione: item.versione || '1.0',
            testo: perEditor(item.testo),
            data_pubblicazione: item.data_pubblicazione || '',
            idSala: refId(item.idSala),
            attivo: parseInt(item.attivo, 10) === 1 ? 1 : 0,
            // in lettura l'API risolve gia' la riga di `resource`
            allegato: item.allegato && item.allegato.hash ? item.allegato : null
        });
    }, [item]);

    function aggiorna(campo, valore) {
        setForm(prev => ({ ...prev, [campo]: valore }));
    }

    /**
     * Il file parte subito, non al salvataggio: cosi' l'errore (formato, peso)
     * arriva mentre l'utente sta ancora guardando il campo. Nel form resta
     * l'hash, che e' l'unica cosa che la colonna `allegato` conserva.
     */
    async function caricaAllegato(e) {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;

        const problema = erroreFile(file);
        if (problema) {
            toast.error(problema, { theme: 'colored' });
            return;
        }

        setCaricando(true);
        try {
            const base64 = await leggiBase64(file);
            const res = await DataManager.uploadFile('regolamento', file.name, base64);
            if (res && res.success === 1 && res.body) {
                aggiorna('allegato', { ...res.body, size: file.size });
                toast.success('Allegato caricato. Salva per collegarlo al regolamento.', { theme: 'colored' });
            }
            else {
                toast.error(messaggioErrore(res, 'Caricamento non riuscito.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error(err.message || 'Caricamento non riuscito.', { theme: 'colored' });
        }
        finally {
            setCaricando(false);
        }
    }

    async function scaricaAllegato() {
        try {
            await apriAllegato(form.allegato);
        }
        catch (err) {
            toast.error(err.message || 'Allegato non recuperabile.', { theme: 'colored' });
        }
    }

    /**
     * Disattiva gli altri regolamenti della stessa sala: la regola vuole un
     * solo regolamento attivo per sala alla volta.
     */
    async function disattivaConcorrenti(idSala, escludiId) {
        const concorrenti = tutti.items.filter(r =>
            r.id !== escludiId
            && parseInt(r.attivo, 10) === 1
            && refId(r.idSala) === idSala
        );
        for (let i = 0; i < concorrenti.length; i++) {
            await DataManager.updateItem('regolamento', { attivo: 0 }, concorrenti[i].id);
        }
        return concorrenti.length;
    }

    async function salva(e) {
        e.preventDefault();
        if (!form.titolo.trim()) {
            toast.error('Il titolo è obbligatorio.', { theme: 'colored' });
            return;
        }
        if (!form.versione.trim()) {
            toast.error('Indica la versione del regolamento.', { theme: 'colored' });
            return;
        }
        if (!form.data_pubblicazione) {
            toast.error('Indica la data di pubblicazione.', { theme: 'colored' });
            return;
        }
        if (parseInt(form.attivo, 10) === 1 && eVuoto(form.testo)) {
            toast.error('Un regolamento attivo deve avere un testo da far accettare.', { theme: 'colored' });
            return;
        }

        const idSala = parseInt(form.idSala, 10) || 0;
        if (!idSala) {
            toast.error('Scegli la sala a cui si applica il regolamento.', { theme: 'colored' });
            return;
        }

        const payload = {
            titolo: form.titolo.trim(),
            versione: form.versione.trim(),
            // ripulito anche qui, non solo a schermo: nel database ci finisce
            // quello che l'editor ha prodotto, e l'editor sta dal lato del client
            testo: pulisci(form.testo),
            data_pubblicazione: form.data_pubblicazione,
            idSala: refPayload(idSala),
            attivo: parseInt(form.attivo, 10),
            // `parseData` di un campo file tiene solo l'hash: '' lo sgancia
            allegato: { hash: form.allegato ? form.allegato.hash : '' }
        };

        setSaving(true);
        try {
            const res = nuovo
                ? await DataManager.insertItem('regolamento', payload)
                : await DataManager.updateItem('regolamento', payload, item.id);

            if (res && res.success === 1) {
                let disattivati = 0;
                if (parseInt(form.attivo, 10) === 1) {
                    disattivati = await disattivaConcorrenti(idSala, nuovo ? null : item.id);
                }
                toast.success(
                    disattivati > 0
                        ? `Regolamento salvato. ${disattivati} versione/i precedente/i disattivata/e.`
                        : 'Regolamento salvato.',
                    { theme: 'colored' }
                );
                navigate('/admin/regolamenti');
            }
            else {
                toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function elimina() {
        setWorking(true);
        try {
            const res = await DataManager.deleteItem('regolamento', item.id);
            if (res && res.success === 1) {
                toast.success('Regolamento eliminato.', { theme: 'colored' });
                navigate('/admin/regolamenti');
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    const stato = statoLista({ loading: loading, error: error, empty: !form, icon: 'file-lines' });

    return (
        <DetailPage
            backTo="/admin/regolamenti"
            backLabel="Torna ai regolamenti"
            eyebrow="Regole di utilizzo"
            title={nuovo ? 'Nuovo regolamento' : (item ? item.titolo : 'Regolamento')}
            subtitle="Il testo qui sotto è quello che il condomino accetta prenotando."
        >
            {stato || (
                <form onSubmit={salva}>
                    <div className="lv-form-grid">
                        <Field label="Titolo" required>
                            <input className="lv-input" value={form.titolo} onChange={e => aggiorna('titolo', e.target.value)} />
                        </Field>
                        <Field label="Versione" required hint="Cambiala quando il testo cambia: resta scritta sulle prenotazioni.">
                            <input
                                className="lv-input"
                                value={form.versione}
                                onChange={e => aggiorna('versione', e.target.value)}
                            />
                        </Field>
                        <Field label="Sala" required hint="Ogni sala ha il suo regolamento: chi prenota vede solo quello della sala scelta.">
                            <Select
                                value={form.idSala}
                                onChange={v => aggiorna('idSala', v)}
                                options={[
                                    { value: '', label: 'Scegli la sala…' },
                                    ...sale.items.map(s => ({ value: String(s.id), label: s.nome }))
                                ]}
                            />
                        </Field>
                        <Field label="Data di pubblicazione" required>
                            <input
                                type="date"
                                className="lv-input"
                                value={form.data_pubblicazione}
                                onChange={e => aggiorna('data_pubblicazione', e.target.value)}
                            />
                        </Field>
                        <Field label="Stato" hint="Attivandolo, gli altri regolamenti della stessa sala passano in archivio.">
                            <Select
                                value={form.attivo}
                                onChange={v => aggiorna('attivo', v)}
                                options={[
                                    { value: '1', label: 'Attivo' },
                                    { value: '0', label: 'Archiviato' }
                                ]}
                            />
                        </Field>
                        <Field
                            label="Testo del decalogo"
                            wide
                            hint="Titoli, elenchi ed evidenziature: il condomino lo legge cosi' com'e' scritto qui."
                        >
                            <RichText
                                ariaLabel="Testo del decalogo"
                                value={form.testo}
                                onChange={v => aggiorna('testo', v)}
                                placeholder="Scrivi qui il decalogo…"
                            />
                        </Field>
                        <Field
                            label="Allegato"
                            wide
                            hint={`Copia ufficiale da scaricare (${ESTENSIONI.join(', ')}, max ${pesoLeggibile(PESO_MASSIMO)}). Quello che il condomino accetta resta il testo qui sopra.`}
                        >
                            {form.allegato ? (
                                <div className="lv-row-actions">
                                    <span className="lv-cell-strong">
                                        {form.allegato.filename}
                                        {form.allegato.size
                                            ? ` · ${pesoLeggibile(form.allegato.size)}`
                                            : ''}
                                    </span>
                                    <button
                                        type="button"
                                        className="lv-btn lv-btn-ghost lv-btn-sm"
                                        onClick={scaricaAllegato}
                                    >
                                        <FontAwesomeIcon icon={['fas', 'download']} className="me-2" />
                                        Apri
                                    </button>
                                    <button
                                        type="button"
                                        className="lv-btn lv-btn-ghost lv-btn-sm lv-btn-danger-ghost"
                                        onClick={() => aggiorna('allegato', null)}
                                    >
                                        Rimuovi
                                    </button>
                                </div>
                            ) : (
                                <input
                                    type="file"
                                    className="lv-input"
                                    accept={ESTENSIONI.map(e => `.${e}`).join(',')}
                                    disabled={caricando}
                                    onChange={caricaAllegato}
                                />
                            )}
                            {caricando && (
                                <span className="lv-cell-sub">
                                    <FontAwesomeIcon icon={['fas', 'circle-notch']} spin className="me-2" />
                                    Caricamento in corso…
                                </span>
                            )}
                        </Field>
                    </div>

                    <DetailActions saving={saving} submitLabel={nuovo ? 'Pubblica' : 'Salva modifiche'}>
                        {!nuovo && (
                            <button
                                type="button"
                                className="lv-btn lv-btn-ghost lv-btn-danger-ghost"
                                onClick={() => setDaEliminare(true)}
                            >
                                <FontAwesomeIcon icon={['fas', 'trash']} className="me-2" />
                                Elimina
                            </button>
                        )}
                    </DetailActions>
                </form>
            )}

            <ConfirmModal
                show={daEliminare}
                title="Eliminare il regolamento?"
                message={item
                    ? `«${item.titolo}» sparirà. Le prenotazioni che ne hanno accettato una versione restano invariate.`
                    : ''}
                confirmLabel="Elimina"
                danger
                working={working}
                onConfirm={elimina}
                onClose={() => setDaEliminare(false)}
            />
        </DetailPage>
    );
}
